import { useMemo, lazy, Suspense, useState, useEffect } from 'react';
import { Text, Flex, Spinner, cn } from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import { AppErrorBoundary } from '@/modules/sentry';
import { type IFileRendererProps, EFileType } from './types';
import { detectFileType, detectFileTypeFromMagicBytes } from './utils';
import { UnsupportedFilePreview } from './components';

// Lazy load all file renderers for better code splitting
// Each renderer is imported from its own file to enable true code splitting
const AudioRenderer = lazy(() =>
  import('./renderers/AudioRenderer').then(m => ({ default: m.AudioRenderer })),
);
const CsvRenderer = lazy(() =>
  import('./renderers/CsvRenderer').then(m => ({ default: m.CsvRenderer })),
);
const DocxRenderer = lazy(() =>
  import('./renderers/DocxRenderer').then(m => ({ default: m.DocxRenderer })),
);
const EmlRenderer = lazy(() =>
  import('./renderers/EmlRenderer').then(m => ({ default: m.EmlRenderer })),
);
const ExcelRenderer = lazy(() =>
  import('./renderers/ExcelRenderer').then(m => ({ default: m.ExcelRenderer })),
);
const FeatherRenderer = lazy(() =>
  import('./renderers/FeatherRenderer').then(m => ({
    default: m.FeatherRenderer,
  })),
);
const HtmlRenderer = lazy(() =>
  import('./renderers/HtmlRenderer').then(m => ({ default: m.HtmlRenderer })),
);
const ImageRenderer = lazy(() =>
  import('./renderers/ImageRenderer').then(m => ({ default: m.ImageRenderer })),
);
const JsonRenderer = lazy(() =>
  import('./renderers/JsonRenderer').then(m => ({ default: m.JsonRenderer })),
);
const MarkdownRenderer = lazy(() =>
  import('./renderers/MarkdownRenderer').then(m => ({
    default: m.MarkdownRenderer,
  })),
);
const PdfRenderer = lazy(() =>
  import('./renderers/PdfRenderer').then(m => ({ default: m.PdfRenderer })),
);
const PythonRenderer = lazy(() =>
  import('./renderers/PythonRenderer').then(m => ({
    default: m.PythonRenderer,
  })),
);
const TextRenderer = lazy(() =>
  import('./renderers/TextRenderer').then(m => ({ default: m.TextRenderer })),
);
const VideoRenderer = lazy(() =>
  import('./renderers/VideoRenderer').then(m => ({ default: m.VideoRenderer })),
);
const YamlRenderer = lazy(() =>
  import('./renderers/YamlRenderer').then(m => ({ default: m.YamlRenderer })),
);

/** File types whose renderer draws its own sheet in the output modal. */
const SELF_SHEETED_TYPES = new Set<EFileType>([
  EFileType.TXT,
  EFileType.CSV,
  EFileType.EXCEL,
  EFileType.JSON,
  EFileType.YML,
  EFileType.PYTHON,
  EFileType.HTML,
  EFileType.MARKDOWN,
]);

/**
 * Main file renderer component that detects file type and routes to appropriate renderer
 *
 * @param props - Component props
 * @param props.blob - The file blob to render
 * @param props.fileName - The name of the file (used for type detection)
 * @param props.metadata - Optional metadata including contentType and fileSize
 * @param props.config - Optional configuration for renderers
 *
 * @remarks
 * - Detects file type using detectFileType() based on fileName and contentType
 * - Routes to appropriate specialized renderer (ImageRenderer, PdfRenderer, etc.)
 * - All renderers are lazy-loaded for code splitting
 * - Shows unsupported file type message for unknown file types
 * - Wrapped in AppErrorBoundary for error handling
 */
export function FileRenderer({
  blob,
  fileName,
  metadata,
  config,
  scrollParent,
  isExpanded,
}: IFileRendererProps) {
  const { t: tCommon } = useTranslation('common');

  // Initial file type detection from fileName and metadata (synchronous)
  const initialFileType = useMemo(
    () => detectFileType(fileName, metadata?.contentType),
    [fileName, metadata?.contentType],
  );

  // State for final file type after magic bytes check
  const [fileType, setFileType] = useState<EFileType>(initialFileType);
  const [isDetecting, setIsDetecting] = useState(true);

  // Async magic bytes detection to override file type if content doesn't match extension
  // eslint-disable-next-line no-restricted-syntax -- reason: Async magic bytes detection requires useEffect for blob content inspection
  useEffect(() => {
    let isCancelled = false;

    const detectFromContent = async () => {
      setIsDetecting(true);

      // Check magic bytes for accurate type detection
      const magicBytesType = await detectFileTypeFromMagicBytes(blob);
      if (!isCancelled) {
        // If magic bytes detection found a type, use it (overrides extension-based detection)
        if (magicBytesType) {
          setFileType(magicBytesType);
        } else {
          setFileType(initialFileType);
        }
        setIsDetecting(false);
      }
    };

    detectFromContent();

    return () => {
      isCancelled = true;
    };
  }, [blob, initialFileType]);

  // Render the appropriate renderer based on file type
  const renderContent = () => {
    // Show loading while detecting file type from magic bytes
    if (isDetecting) {
      if (config?.hideLoadingIndicator) return null;
      return (
        <Flex
          width="full"
          gap={2}
          align="center"
          justify="center"
          className="p-4"
        >
          <Spinner size="sm" />
          <Text level="small" color="muted">
            {tCommon('common.loading')}
          </Text>
        </Flex>
      );
    }

    const rendererProps = { blob, fileName, config, scrollParent, isExpanded };

    switch (fileType) {
      case EFileType.IMG:
      case EFileType.GIF:
        return (
          <Suspense fallback={null}>
            <ImageRenderer {...rendererProps} />
          </Suspense>
        );

      case EFileType.PDF:
        return (
          <Suspense fallback={null}>
            <PdfRenderer {...rendererProps} />
          </Suspense>
        );

      case EFileType.CSV:
        return (
          <Suspense fallback={null}>
            <CsvRenderer {...rendererProps} />
          </Suspense>
        );

      case EFileType.TXT:
        return (
          <Suspense fallback={null}>
            <TextRenderer {...rendererProps} />
          </Suspense>
        );

      case EFileType.JSON:
        return (
          <Suspense fallback={null}>
            <JsonRenderer {...rendererProps} />
          </Suspense>
        );

      case EFileType.YML:
        return (
          <Suspense fallback={null}>
            <YamlRenderer {...rendererProps} />
          </Suspense>
        );

      case EFileType.PYTHON:
        return (
          <Suspense fallback={null}>
            <PythonRenderer {...rendererProps} />
          </Suspense>
        );

      case EFileType.EXCEL:
        return (
          <Suspense fallback={null}>
            <ExcelRenderer {...rendererProps} />
          </Suspense>
        );

      case EFileType.FEATHER:
        return (
          <Suspense fallback={null}>
            <FeatherRenderer {...rendererProps} />
          </Suspense>
        );

      case EFileType.DOCX:
        return (
          <Suspense fallback={null}>
            <DocxRenderer {...rendererProps} />
          </Suspense>
        );

      case EFileType.HTML:
        return (
          <Suspense fallback={null}>
            <HtmlRenderer {...rendererProps} />
          </Suspense>
        );

      case EFileType.MARKDOWN:
        return (
          <Suspense fallback={null}>
            <MarkdownRenderer {...rendererProps} />
          </Suspense>
        );

      case EFileType.EML:
        return (
          <Suspense fallback={null}>
            <EmlRenderer {...rendererProps} />
          </Suspense>
        );

      case EFileType.AUDIO:
        return (
          <Suspense fallback={null}>
            <AudioRenderer {...rendererProps} />
          </Suspense>
        );

      case EFileType.VIDEO:
        return (
          <Suspense fallback={null}>
            <VideoRenderer {...rendererProps} />
          </Suspense>
        );

      case EFileType.UNSUPPORTED:
      case EFileType.UNKNOWN:
      default:
        return <UnsupportedFilePreview blob={blob} fileName={fileName} />;
    }
  };

  // The image container fills the modal body edge-to-edge with no sheet, so
  // it opts out of the padding every other expanded file type gets here.
  const isImage = fileType === EFileType.IMG || fileType === EFileType.GIF;
  // These renderers draw their own sheet when output-presented, so this wrapper must not double the padding.
  const hasOwnSheet =
    config?.outputPresentation === true && SELF_SHEETED_TYPES.has(fileType);

  return (
    <AppErrorBoundary>
      {/* `h-full` so a renderer that fills its parent (the HTML iframe, the PDF
          canvas) can actually reach the height a sized container offers. With a
          width-only wrapper the chain broke here and those renderers fell back
          to their own minimum height inside a much taller dialog. */}
      <div
        className={cn(
          'file-renderer h-full w-full',
          isExpanded && !isImage && !hasOwnSheet && 'p-6',
        )}
      >
        {renderContent()}
      </div>
    </AppErrorBoundary>
  );
}
